import { keyframes, styled } from "goober";
import type { HTMLAttributeAnchorTarget, ReactElement, ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useIsMobile } from "../hooks/useIsMobile";
import { subscribeErrorToasts } from "../lib/errorToast";
import { playPop } from "../lib/sounds";
import { Button } from "./Button";
import { ButtonLink } from "./Button/ButtonLink";
import { Icon, type IconName } from "./Icon";
import { Text } from "./Text";

const toastButtonStyle = `
  && {
    background: var(--alpha4);
    margin-left: 6px;
  }
  &&:hover:not(:disabled) {
    background: var(--alpha8);
  }
`;

const ToastButton = styled(Button)`
  ${toastButtonStyle}
`;

const ToastButtonLink = styled(ButtonLink)`
  ${toastButtonStyle}
`;

const slideIn = keyframes`
  0% {
    transform: translateX(-50%) translateY(-100%);
    opacity: 0;
  }
  60% {
    transform: translateX(-50%) translateY(2px);
    opacity: 1;
  }
  100% {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
`;

const scaleOut = keyframes`
  from {
    transform: translateX(-50%) scale(1);
    opacity: 1;
  }
  to {
    transform: translateX(-50%) scale(0.92);
    opacity: 0;
  }
`;

const PADDING = 6;
const DISMISS_MS = 60;

const Wrapper = styled("div")`
  position: fixed;
  inset: auto;
  left: 50%;
  transform: translateX(-50%);
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  overflow: visible;
  width: max-content;

  transition: top 150ms ease;

  &.entering {
    animation: ${slideIn} 300ms cubic-bezier(0.22, 0.68, 0.35, 1.05);
  }
  &.dismissing {
    animation: ${scaleOut} ${DISMISS_MS}ms ease-in forwards;
  }
`;

const Container = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-primary);
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-low);
  border-radius: 12px;
  padding-left: 10px;
  padding-top: ${PADDING}px;
  padding-bottom: ${PADDING}px;
  padding-right: ${PADDING}px;
  white-space: nowrap;
`;

// --- Toast component ---

const AUTO_DISMISS_MS = 4000;

type ToastVariant = "default" | "error";

type ToastConfig = {
  icon: IconName | ReactElement;
  message: string;
  buttonLabel: string;
  onButtonClick: () => void;
  buttonTo?: string;
  buttonTarget?: HTMLAttributeAnchorTarget;
  variant: ToastVariant;
  persist: boolean;
  dismissible: boolean;
  onClose: () => void;
};

const ErrorContainer = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--orange-1000);
  background: var(--orange-200);
  outline: 0.5px solid var(--orange-400);
  box-shadow: var(--elevation-low);
  border-radius: 12px;
  padding-left: 10px;
  padding-top: ${PADDING}px;
  padding-bottom: ${PADDING}px;
  padding-right: ${PADDING}px;
  white-space: nowrap;
`;

const errorToastButtonStyle = `
  && {
    background: var(--orange-400);
    color: var(--orange-1000);
    margin-left: 6px;
  }
  &&:hover:not(:disabled) {
    background: color-mix(in oklab, var(--orange-400), var(--orange-600) 20%);
  }
`;

const ErrorToastButton = styled(Button)`
  ${errorToastButtonStyle}
`;

const ErrorToastButtonLink = styled(ButtonLink)`
  ${errorToastButtonStyle}
`;

const STACK_ROW_PX = 44;

export function ToastCard({
  icon,
  message,
  buttonLabel,
  onButtonClick,
  buttonTo,
  buttonTarget,
  variant = "default",
  fullWidth = false,
  onDismiss,
}: {
  icon: IconName | ReactElement;
  message: string;
  buttonLabel: string;
  onButtonClick: () => void;
  buttonTo?: string;
  buttonTarget?: HTMLAttributeAnchorTarget;
  variant?: ToastVariant;
  fullWidth?: boolean;
  onDismiss?: () => void;
}) {
  const Outer = variant === "error" ? ErrorContainer : Container;
  const Btn = variant === "error" ? ErrorToastButton : ToastButton;
  const BtnLink = variant === "error" ? ErrorToastButtonLink : ToastButtonLink;
  return (
    <Outer style={fullWidth ? { width: "100%" } : undefined}>
      {typeof icon === "string" ? <Icon name={icon} size={16} /> : icon}
      <Text variant="text-regular">{message}</Text>
      {buttonTo ? (
        <BtnLink
          to={buttonTo}
          target={buttonTarget}
          rel={buttonTarget === "_blank" ? "noopener noreferrer" : undefined}
          variant="secondary"
          size="m"
          onClick={onButtonClick}
          style={fullWidth ? { marginLeft: "auto" } : undefined}
        >
          {buttonLabel}
        </BtnLink>
      ) : (
        <Btn
          variant="secondary"
          size="m"
          onClick={onButtonClick}
          style={fullWidth ? { marginLeft: "auto" } : undefined}
        >
          {buttonLabel}
        </Btn>
      )}
      {onDismiss && (
        <Button
          variant="subtle"
          size="m"
          icon="x"
          onClick={onDismiss}
          aria-label="Dismiss"
        />
      )}
    </Outer>
  );
}

function Toast({
  icon,
  message,
  buttonLabel,
  onButtonClick,
  onClose,
  buttonTo,
  buttonTarget,
  variant,
  persist,
  dismissible,
  stackIndex,
}: ToastConfig & { stackIndex: number }) {
  const isMobile = useIsMobile();
  const played = useRef(false);
  const [dismissing, setDismissing] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (el && !el.matches(":popover-open")) el.showPopover();
  }, []);

  useEffect(() => {
    if (!played.current) {
      played.current = true;
      playPop();
    }
  }, []);

  const dismiss = useCallback(() => {
    setDismissing(true);
    setTimeout(onButtonClick, DISMISS_MS);
  }, [onButtonClick]);

  const close = useCallback(() => {
    setDismissing(true);
    setTimeout(onClose, DISMISS_MS);
  }, [onClose]);

  useEffect(() => {
    if (persist) return;
    const timer = setTimeout(close, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [close, persist]);

  return (
    <Wrapper
      ref={wrapperRef}
      popover="manual"
      style={{ top: (isMobile ? 6 : 10) + stackIndex * STACK_ROW_PX }}
      className={dismissing ? "dismissing" : "entering"}
    >
      <ToastCard
        icon={icon}
        message={message}
        buttonLabel={buttonLabel}
        onButtonClick={dismiss}
        buttonTo={buttonTo}
        buttonTarget={buttonTarget}
        variant={variant}
        onDismiss={dismissible ? close : undefined}
      />
    </Wrapper>
  );
}

// --- Context API ---

type ShowToastArgs = {
  icon: IconName | ReactElement;
  message: string;
  buttonLabel: string;
  onButtonClick?: () => void;
  buttonTo?: string;
  buttonTarget?: HTMLAttributeAnchorTarget;
  variant?: ToastVariant;
  persist?: boolean;
  dismissible?: boolean;
  // Showing again with the same key updates that toast in place instead of
  // stacking a duplicate; hideToast(key) removes it.
  key?: string;
};

type ToastApi = {
  showToast: (args: ShowToastArgs) => void;
  hideToast: (key: string) => void;
};

const ToastContext = createContext<ToastApi>({
  showToast: () => {},
  hideToast: () => {},
});

export function useToast() {
  return useContext(ToastContext);
}

const ERROR_TOAST_DEDUPE_MS = 3000;

type ActiveToast = ToastConfig & { id: number; dedupeKey?: string };

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const nextId = useRef(1);

  const showToast = useCallback((args: ShowToastArgs) => {
    setToasts((prev) => {
      const { key: dedupeKey, ...config } = args;
      const existing = args.key
        ? prev.find((t) => t.dedupeKey === args.key)
        : undefined;
      const id = existing?.id ?? nextId.current++;
      const remove = () => setToasts((p) => p.filter((t) => t.id !== id));
      const entry: ActiveToast = {
        ...config,
        id,
        dedupeKey,
        variant: args.variant ?? "default",
        persist: args.persist ?? false,
        dismissible: args.dismissible ?? false,
        onClose: remove,
        onButtonClick: () => {
          args.onButtonClick?.();
          remove();
        },
      };
      return existing
        ? prev.map((t) => (t.id === id ? entry : t))
        : [...prev, entry];
    });
  }, []);

  const hideToast = useCallback((key: string) => {
    setToasts((prev) => prev.filter((t) => t.dedupeKey !== key));
  }, []);

  useEffect(() => {
    let lastMessage: string | null = null;
    let lastShownAt = 0;
    return subscribeErrorToasts((args) => {
      const now = performance.now();
      if (
        args.message === lastMessage &&
        now - lastShownAt < ERROR_TOAST_DEDUPE_MS
      ) {
        return;
      }
      lastMessage = args.message;
      lastShownAt = now;
      showToast({ ...args, variant: "error" });
    });
  }, [showToast]);

  const api = useMemo(() => ({ showToast, hideToast }), [showToast, hideToast]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {toasts.map((t, i) => (
        <Toast key={t.id} {...t} stackIndex={toasts.length - 1 - i} />
      ))}
    </ToastContext.Provider>
  );
}
